/**
 * REQUEST MANAGER
 * Gestionnaire de requêtes HTTP avec cache et serializers
 */

import ENDPOINTS from './endpoints.js';

class RequestManager {
  constructor() {
    this.cache = new Map();
    this.cacheTTL = 1000; // 1 seconde de cache
    this.baseURL = 'https://apis.1xpay.co';
  }

  /**
   * Effectue une requête HTTP GET
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Effectue une requête HTTP POST
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body: JSON.stringify(data) });
  }

  /**
   * Effectue une requête HTTP PUT
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body: JSON.stringify(data) });
  }

  /**
   * Effectue une requête HTTP PATCH
   */
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(data) });
  }

  /**
   * Effectue une requête HTTP DELETE
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Méthode principale de requête
   */
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    const cacheKey = `${options.method || 'GET'}:${url}`;

    // Vérifier le cache pour les requêtes GET
    if ((options.method === 'GET' || !options.method) && options.cache !== false) {
      const cached = this.getCached(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: options.body,
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Mettre en cache pour les requêtes GET réussies
      if ((options.method === 'GET' || !options.method) && options.cache !== false) {
        this.setCache(cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error('Request error:', error);
      throw error;
    }
  }

  /**
   * Récupère depuis le cache
   */
  getCached(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  /**
   * Met en cache
   */
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Vide le cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Récupère les abonnements
   */
  async getSubscriptions() {
    return this.get(ENDPOINTS.PRODUCTS.SUBSCRIPTIONS);
  }

  /**
   * Récupère les cartes cadeaux
   */
  async getGiftCards() {
    return this.get(ENDPOINTS.PRODUCTS.GIFTCARDS);
  }

  /**
   * Récupère un produit par ID
   */
  async getProduct(id) {
    return this.get(ENDPOINTS.PRODUCTS.PRODUCT_DETAIL(id));
  }

  /**
   * Lance un paiement
   */
  async launchPayment(paymentData) {
    return this.post(ENDPOINTS.PAYMENT.LAUNCH, paymentData);
  }

  /**
   * Vérifie le statut d'un paiement
   */
  async checkPaymentStatus(transactionId) {
    return this.get(ENDPOINTS.PAYMENT.STATUS(transactionId));
  }

  /**
   * Résout l'opérateur téléphonique
   */
  async resolveCarrier(phoneNumber) {
    return this.get(ENDPOINTS.CARRIER.RESOLVE(phoneNumber));
  }
}

// Instance singleton
const requestManager = new RequestManager();

export default requestManager;
